import Konva from 'konva';
import {Circle, Line, Rect} from 'react-konva';
import {KonvaEventObject} from 'konva/types/Node';
import {KonvaPointerEvent} from 'konva/types/PointerEvents';
import React, {useState, useCallback, useEffect, useRef} from 'react';

import DeleteIconTag from './Tag';
import {BOX_LABEL_DEFAULT, BOX_LABEL_SELECTED, genId} from './const';
import {LabelPropsEvent, LinePropsEvent, Point, PolyLabelProps} from './types';

type AnchorProps = {
  point: Point;
  fill: string;
  onClick: () => void;
  onMouseOver: () => void;
  onMouseOut: () => void;
};

function Anchor(props: AnchorProps) {
  const [strokeWidth, setStrokeWidth] = useState(2);

  return (
    <Circle
      radius={10}
      stroke="#666"
      x={props.point.x}
      y={props.point.y}
      fill={props.fill}
      strokeWidth={strokeWidth}
      onMouseOver={() => {
        document.body.style.cursor = 'pointer';
        setStrokeWidth(3);
        props.onMouseOver();
      }}
      onMouseOut={() => {
        document.body.style.cursor = 'default';
        setStrokeWidth(2);
        props.onMouseOut();
      }}
      onClick={() => {
        document.body.style.cursor = 'default';
        props.onClick();
      }}
    />
  );
}

type PolygonOriginAnchorProps = {
  point: Point;
  onValidClick: () => void;
  onValidMouseOver: () => void;
  validateMouseEvents: () => boolean;
};

function PolygonOriginAnchor(props: PolygonOriginAnchorProps) {
  const [fill, setFill] = useState('transparent');

  return (
    <Anchor
      fill={fill}
      point={props.point}
      onClick={() => {
        if (props.validateMouseEvents()) {
          props.onValidClick();
        }
      }}
      onMouseOver={() => {
        if (props.validateMouseEvents()) {
          document.body.style.cursor = 'pointer';
          setFill('green');
          props.onValidMouseOver();
        } else {
          document.body.style.cursor = 'not-allowed';
          setFill('red');
        }
      }}
      onMouseOut={() => {
        setFill('transparent');
      }}
    />
  );
}

type PolygonProps = {
  mousePosition: Point;
  isActiveTool: boolean;

  width?: number;
  height?: number;
  selected?: boolean;
  complete?: boolean;
  data?: PolyLabelProps;
  closeTargetRadius?: number;
  onComplete: (data: PolyLabelProps) => void;
};

export function Polygon({
  width,
  height,
  isActiveTool,
  mousePosition,
  selected = false,
  complete = false,
  data = {id: '', points: []},

  onClick = () => {},
  onDelete = () => {},
  onComplete = () => {},
  onContextMenu = () => {},
}: PolygonProps & LabelPropsEvent & LinePropsEvent) {
  const rectRef = useRef<Konva.Rect>(null);
  const [points, setPoints] = useState<Point[]>([]);
  const [nextPoint, setNextPoint] = useState<Point>({x: 0, y: 0});
  const [tagCenter, setTagCenter] = useState<Point>({x: 0, y: 0});

  const [isHover, setIsHover] = useState(false);
  const [labelStyle, setLabelStyle] = useState(BOX_LABEL_DEFAULT);

  const [isComplete, setIsComplete] = useState(complete);

  const onPointDraw = useCallback(
    ({x, y}: Point) => {
      setPoints([...points, {x, y}]);
    },
    [points]
  );

  const onLocalComplete = useCallback(() => {
    onComplete({...data, id: genId('POLY'), points: points, complete: true});
    setNextPoint(points[0]);
    setIsComplete(true);
  }, [data, onComplete, points]);

  const onLocalContextMenu = useCallback(
    (event: KonvaPointerEvent) => {
      event.evt.preventDefault();
      onContextMenu(event);
    },
    [onContextMenu]
  );

  const onPolyClick = useCallback(
    (event: KonvaEventObject<MouseEvent>) => {
      event.evt.preventDefault();
      onClick(event);
    },
    [onClick]
  );

  const onMouse = useCallback((isHovering: boolean) => {
    setIsHover(isHovering);
  }, []);

  useEffect(() => {
    if (data) {
      const vertices = data.points;

      setPoints(vertices);

      if (vertices.length) {
        const numberOfVertices = vertices.length;
        let polygonArea = 0;
        let centroidX = 0;
        let centroidY = 0;

        for (let index = 0; index < numberOfVertices; index++) {
          const currentX = vertices[index].x;
          const currentY = vertices[index].y;
          const nextX = vertices[(index + 1) % numberOfVertices].x;
          const nextY = vertices[(index + 1) % numberOfVertices].y;

          const crossProduct = currentX * nextY - nextX * currentY;

          polygonArea = polygonArea + crossProduct;
          centroidX = centroidX + (currentX + nextX) * crossProduct;
          centroidY = centroidY + (currentY + nextY) * crossProduct;
        }

        polygonArea = polygonArea / 2;
        centroidX = centroidX / (6 * polygonArea);
        centroidY = centroidY / (6 * polygonArea);

        setTagCenter({x: centroidX, y: centroidY});
      }
    }
  }, [data]);

  useEffect(() => {
    setLabelStyle(isHover || selected ? BOX_LABEL_SELECTED : BOX_LABEL_DEFAULT);
  }, [isHover, selected]);

  const isCursorPointInsidePolygon = useCallback(
    (event: KonvaEventObject<MouseEvent>) => {
      let inside = false;
      const cursorPointX = event.evt.offsetX;
      const cursorPointY = event.evt.offsetY;

      for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
        const xi = points[i].x;
        const yi = points[i].y;
        const xj = points[j].x;
        const yj = points[j].y;

        const intersect =
          yi > cursorPointY !== yj > cursorPointY &&
          cursorPointX < ((xj - xi) * (cursorPointY - yi)) / (yj - yi) + xi;

        if (intersect) {
          inside = !inside;
        }
      }

      return inside;
    },
    [points]
  );

  useEffect(() => {
    if (isActiveTool && !isComplete) {
      setNextPoint(mousePosition);
    }
  }, [isActiveTool, isComplete, mousePosition]);

  return (
    <>
      <Line
        lineJoin="round"
        {...labelStyle}
        closed={isComplete}
        onClick={onPolyClick}
        onContextMenu={onLocalContextMenu}
        points={points
          .flatMap(point => [point.x, point.y])
          .concat(isComplete ? [] : [nextPoint.x, nextPoint.y])}
        onMouseEnter={event => {
          onMouse(isCursorPointInsidePolygon(event));
        }}
        onMouseLeave={event => {
          onMouse(isCursorPointInsidePolygon(event));
        }}
      />

      {/* Hit Area */}
      {!isComplete && (
        <Rect
          x={0}
          y={0}
          ref={rectRef}
          width={width}
          height={height}
          onClick={event => {
            const x = event.evt.offsetX;
            const y = event.evt.offsetY;

            onPointDraw({x, y});
          }}
        />
      )}

      {points[0] && !isComplete && (
        <PolygonOriginAnchor
          point={points[0]}
          onValidClick={onLocalComplete}
          onValidMouseOver={() => {
            setNextPoint(points[0]);
          }}
          validateMouseEvents={() => points.length > 2}
        />
      )}
      {isComplete && (
        <DeleteIconTag
          id={data.id}
          label={data.label}
          {...tagCenter}
          selected={selected || isHover}
          yBuffer={20}
          onDelete={onDelete}
          onClick={onPolyClick}
          onContextMenu={onLocalContextMenu}
        />
      )}
    </>
  );
}
